import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  ContentCopy,
  Delete,
  Domain,
  Error,
  HelpOutline,
  Language,
  Refresh,
  Settings,
  VerifiedUser,
} from "@mui/icons-material";
import { DangerZone } from "../SiteDetailExtras";
import Interceptor from "../../services/Interceptor";
import ConfirmDialog from "../ConfirmDialog";
import DnsSetupGuideDialog from "../dialog/DnsSetupGuideDialog";

/**
 * Custom Domain Management Section
 */
const CustomDomainSection = ({ site, siteId, onShowMessage, onSiteUpdate }) => {
  const [customDomain, setCustomDomain] = useState(site?.customDomain || "");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [dnsInstructions, setDnsInstructions] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [error, setError] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const handleAddDomain = async () => {
    if (!customDomain.trim()) {
      setError("Please enter a domain");
      return;
    }

    setIsLoading(true);
    setError(null);
    setDnsInstructions(null);

    try {
      const response = await Interceptor.put(`/sites/${siteId}/domain`, {
        customDomain: customDomain.trim(),
      });

      if (response.data.success) {
        setDnsInstructions(response.data.data.dnsInstructions);
        onShowMessage("Custom domain configured! Please set up DNS records.");
        if (onSiteUpdate) {
          onSiteUpdate({
            ...site,
            customDomain: response.data.data.customDomain,
          });
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to configure domain");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveDomain = async () => {
    setRemoveConfirmOpen(false);
    setIsLoading(true);
    setError(null);

    try {
      const response = await Interceptor.put(`/sites/${siteId}/domain`, {
        removeCustomDomain: true,
      });

      if (response.data.success) {
        setCustomDomain("");
        setDnsInstructions(null);
        setVerificationResult(null);
        onShowMessage("Custom domain removed successfully");
        if (onSiteUpdate) {
          onSiteUpdate({
            ...site,
            customDomain: null,
            domain: response.data.data.domain,
          });
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove domain");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyDomain = async () => {
    setIsVerifying(true);
    setVerificationResult(null);

    try {
      const response = await Interceptor.get(`/sites/${siteId}/verify-domain`);
      setVerificationResult(response.data.data);
      // Refresh site data so UI reflects verified/activated status
      if (response.data.data?.verified && onSiteUpdate) {
        onSiteUpdate();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to verify domain");
    } finally {
      setIsVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    onShowMessage("Copied to clipboard!");
  };

  const sslEnabled = process.env.REACT_APP_SSL_ENABLED === "true";
  const protocol = sslEnabled ? "https" : "http";

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        <Language sx={{ mr: 1, verticalAlign: "middle" }} />
        Custom Domain
        <Tooltip title="DNS Setup Guide">
          <IconButton
            size="small"
            onClick={() => setGuideOpen(true)}
            color="primary"
            sx={{ ml: 1 }}
          >
            <HelpOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connect your own domain to this site. Your site will be accessible via
        both the generated subdomain and your custom domain.
      </Typography>

      {/* Current Domain Info */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {site?.customDomain && site?.metadata?.domainVerified
                ? "Primary Domain"
                : "Generated Domain"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body1" fontFamily="monospace">
                {site?.customDomain && site?.metadata?.domainVerified
                  ? site.customDomain
                  : site?.domain}
              </Typography>
              <Tooltip title="Copy URL">
                <IconButton
                  size="small"
                  onClick={() =>
                    copyToClipboard(
                      `${protocol}://${site?.customDomain && site?.metadata?.domainVerified ? site.customDomain : site?.domain}`,
                    )
                  }
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
              {site?.customDomain && site?.metadata?.domainVerified && (
                <Chip
                  size="small"
                  icon={<VerifiedUser />}
                  label="Verified"
                  color="success"
                />
              )}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {site?.customDomain && site?.metadata?.domainVerified
                ? "Also Accessible Via"
                : "Custom Domain"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {site?.customDomain && site?.metadata?.domainVerified ? (
                <>
                  <Typography variant="body1" fontFamily="monospace">
                    {site.domain}
                  </Typography>
                  <Tooltip title="Copy URL">
                    <IconButton
                      size="small"
                      onClick={() =>
                        copyToClipboard(`${protocol}://${site.domain}`)
                      }
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Chip size="small" label="Subdomain" variant="outlined" />
                </>
              ) : site?.customDomain ? (
                <>
                  <Typography variant="body1" fontFamily="monospace">
                    {site.customDomain}
                  </Typography>
                  <Chip
                    size="small"
                    icon={<Error />}
                    label="Pending DNS"
                    color="warning"
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not configured
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Add/Update Domain */}
      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Custom Domain"
            placeholder="example.com"
            value={customDomain}
            onChange={(e) => setCustomDomain(e.target.value)}
            disabled={isLoading}
            helperText="Enter domain without https:// or www"
            error={!!error}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: "flex", gap: 1, mt: { xs: 0, md: 1 } }}>
            <Button
              variant="contained"
              startIcon={
                isLoading ? <CircularProgress size={20} /> : <Domain />
              }
              onClick={handleAddDomain}
              disabled={isLoading || !customDomain.trim()}
            >
              {site?.customDomain ? "Update Domain" : "Add Domain"}
            </Button>
            {site?.customDomain && (
              <>
                <Button
                  variant="outlined"
                  startIcon={
                    isVerifying ? <CircularProgress size={20} /> : <Refresh />
                  }
                  onClick={handleVerifyDomain}
                  disabled={isVerifying}
                >
                  Verify DNS
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setRemoveConfirmOpen(true)}
                  disabled={isLoading}
                >
                  Remove
                </Button>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
        onConfirm={handleRemoveDomain}
        title="Remove Custom Domain"
        message="Are you sure you want to remove the custom domain? Traffic will revert to the generated domain."
        confirmText="Remove"
        confirmColor="error"
      />

      {/* Error Display */}
      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      </Collapse>

      {/* DNS Instructions */}
      <Collapse in={!!dnsInstructions}>
        <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: "action.hover" }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            DNS Configuration Required
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {dnsInstructions?.message}
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dnsInstructions?.records
                ?.filter((r) => !r.optional)
                .map((record, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Chip label={record.type} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography fontFamily="monospace">
                        {record.host}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography fontFamily="monospace">
                          {record.value}
                        </Typography>
                        <Tooltip title="Copy">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(record.value)}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {record.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          {dnsInstructions?.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Important Notes:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {dnsInstructions.notes.map((note, idx) => (
                  <li key={idx}>
                    <Typography variant="body2" color="text.secondary">
                      {note}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}

          <Button
            size="small"
            sx={{ mt: 2 }}
            onClick={() => setGuideOpen(true)}
            startIcon={<HelpOutline />}
          >
            DNS Setup Guide (GoDaddy, Namecheap, Hostinger)
          </Button>
        </Paper>
      </Collapse>

      {/* Verification Result */}
      <Collapse in={!!verificationResult}>
        <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            {verificationResult?.verified ? (
              <>
                <CheckCircle color="success" />
                <Typography
                  variant="subtitle1"
                  color="success.main"
                  fontWeight={600}
                >
                  Domain Verified & Activated!
                </Typography>
              </>
            ) : (
              <>
                <Error color="warning" />
                <Typography
                  variant="subtitle1"
                  color="warning.main"
                  fontWeight={600}
                >
                  DNS Not Fully Configured
                </Typography>
              </>
            )}
          </Box>

          {verificationResult?.activated && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Your custom domain has been activated. SSL certificate will be
              provisioned automatically. It may take a few minutes for HTTPS to
              become available.
            </Alert>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Record</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Found Values</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {verificationResult?.records?.map((record, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Chip label={record.type} size="small" />
                  </TableCell>
                  <TableCell>{record.host}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={record.status}
                      color={
                        record.status === "correct"
                          ? "success"
                          : record.status === "not_found"
                            ? "error"
                            : "warning"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {record.values?.join(", ") || record.error || "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {verificationResult?.issues?.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Issues Found:</Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {verificationResult.issues.map((issue, idx) => (
                  <li key={idx}>{issue}</li>
                ))}
              </ul>
            </Alert>
          )}
        </Paper>
      </Collapse>

      <DnsSetupGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        serverIp={
          dnsInstructions?.records?.find((r) => r.type === "A")?.value ||
          site?.serverIp ||
          ""
        }
        cnameTarget={
          dnsInstructions?.cnameTarget ||
          process.env.REACT_APP_CNAME_TARGET ||
          ""
        }
        customDomain={customDomain || site?.customDomain || ""}
      />
    </Box>
  );
};

/**
 * Site settings tab with site configuration
 */
const SiteSettingsTab = ({
  site,
  siteId,
  onUpdateSite,
  onShowMessage,
  onRefreshSite,
}) => {
  const [localSite, setLocalSite] = useState(site);
  const [saving, setSaving] = useState(false);
  const siteNameRef = React.useRef(null);
  const descriptionRef = React.useRef(null);

  // Sync local state when site prop updates (e.g., after a save)
  React.useEffect(() => {
    if (site) {
      setLocalSite(site);
    }
  }, [site]);

  const handleSiteUpdate = (updatedSite) => {
    if (updatedSite) {
      setLocalSite(updatedSite);
    }
    // Always re-fetch from server to keep Redux store in sync
    if (onRefreshSite) {
      onRefreshSite();
    }
  };

  const currentSite = localSite || site;

  return (
    <CardContent>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Site Settings
      </Typography>
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Site Name"
            defaultValue={currentSite?.name}
            inputRef={siteNameRef}
            key={`name-${currentSite?.name}`}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Domain"
            value={currentSite?.customDomain || currentSite?.domain || ""}
            disabled
            helperText="Manage domain in the Custom Domain section below"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            defaultValue={
              currentSite?.metadata?.description ||
              "WordPress site powered by WP Host"
            }
            inputRef={descriptionRef}
            key={`desc-${currentSite?.metadata?.description}`}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Settings />
              )
            }
            disabled={saving}
            onClick={async () => {
              try {
                setSaving(true);
                const updates = {};
                const newName = siteNameRef.current?.value?.trim();
                const newDescription = descriptionRef.current?.value?.trim();
                const currentDescription =
                  currentSite?.metadata?.description ||
                  "WordPress site powered by WP Host";

                if (newName && newName !== currentSite?.name) {
                  updates.name = newName;
                }
                if (newDescription !== currentDescription) {
                  updates.metadata = {
                    ...currentSite?.metadata,
                    description: newDescription,
                  };
                }

                if (Object.keys(updates).length > 0) {
                  await onUpdateSite(siteId, updates);
                  onShowMessage("Site settings updated successfully!");
                } else {
                  onShowMessage("No changes detected", "info");
                }
              } catch (error) {
                onShowMessage(
                  `Failed to update settings: ${error.message}`,
                  "error",
                );
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Custom Domain Management */}
      <CustomDomainSection
        site={currentSite}
        siteId={siteId}
        onShowMessage={onShowMessage}
        onSiteUpdate={handleSiteUpdate}
      />

      <Divider sx={{ my: 4 }} />

      <DangerZone site={currentSite} siteId={siteId} />
    </CardContent>
  );
};

export default SiteSettingsTab;
